import isEmpty from 'lodash/isEmpty'

import { useIntl } from 'react-intl'
import { MobileSortBy, Table } from 'components/Table'
import { useMemo, useState } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import { useAppSelector } from 'app/hooks'
import { SubscriptionHistoryConfig } from '../types'
import { Spinner } from 'components/Loader'
import { subscriptionHistoryIsFetching } from 'features/home/selectors'
import { useSelector } from 'react-redux'
import { SpinnerContainer } from '../../HomeStatus/SubInfo/ConnectedApps/styles'
import { DATETIME_SHORT, formatTime } from 'app/utils/formatDate'
import { sortByPaymentMethod } from '../utils'
import MobileHistoryViewComponent from './mobileHistoryViewComponent'
import { hasSubscriptionPermissionSelector } from 'features/auth/permissions/selectors'
import {
  EmptyHistoryBlock,
  EmptyHistoryContainer,
  EmptyHistoryDescription,
  EmptyHistoryTitle,
  HistoryMobileHeader,
} from '../SubscriptionHistory/styles'
import { SubscriptionDateLabel, SubscriptionHistoryPlanLabel } from './styles'
import { subscriptionHistoryMessages } from './messages'
import { HISTORY_HEADER } from './consts'
import { getSortOptions } from 'components/Table/utils'
import { SubscriptionHistoryPlanIcon } from 'components/Icons/SubscriptionIcon'
import { Last4Digits, PaymentSystem } from 'components/Card/styles'
import { makePublicUrl } from 'app/utils/publicUrl'
import { TableWithPaginationWrapper } from 'components/Table/styles'
import Image from 'components/Image'

const DEFAULT_SORT_BY = { id: 'date', desc: true }

interface SubscriptionHistoryTableProps {
  data: SubscriptionHistoryConfig[]
}

// TODO: Localization
const PlanCell = (cell: Cell<SubscriptionHistoryConfig>) => {
  const config = cell.row.original
  //const { planName } = config
  //console.log(cell)

  //const { planName: planHistoryName, status } = getHistoryPlanAndName(planName)

  return (
    <>
      <SubscriptionHistoryPlanIcon
        status={config.event}
        planName={config.planName}
      />
      <SubscriptionHistoryPlanLabel>
        {config.description}
      </SubscriptionHistoryPlanLabel>
    </>
  )
}

const PaymentInfoCell = (cell: Cell<SubscriptionHistoryConfig>) => {
  const config = cell.row.original
  const { paymentInfo } = config
  return (
    <>
      {paymentInfo && !isEmpty(paymentInfo) ? (
        <>
          <PaymentSystem
            src={makePublicUrl(
              `images/paymentLogos/${paymentInfo.cardBrand}.svg`
            )}
            alt={paymentInfo.cardBrand}
          />
          <Last4Digits
            isNonBold
            isSmallFont
          >{`**** ${paymentInfo.last4}`}</Last4Digits>
        </>
      ) : (
        '-'
      )}
    </>
  )
}

const DateCell = (cell: Cell<SubscriptionHistoryConfig>) => {
  const config = cell.row.original
  const { date } = config
  return (
    <>
      <SubscriptionDateLabel>
        {date ? formatTime(date, DATETIME_SHORT) : ''}
      </SubscriptionDateLabel>
    </>
  )
}

function SubscriptionHistoryTable(props: SubscriptionHistoryTableProps) {
  const isFetching = useSelector(subscriptionHistoryIsFetching)
  const hasSubscriptionPermission = useAppSelector(
    hasSubscriptionPermissionSelector
  )

  const { formatMessage } = useIntl()

  const headers = useMemo(() => {
    const headersMap = {
      date: {
        label: formatMessage(subscriptionHistoryMessages.dateHeader),
        id: HISTORY_HEADER.DATE,
      },
      planName: {
        label: formatMessage(subscriptionHistoryMessages.planHeader),
        id: HISTORY_HEADER.PLAN,
      },
      canceledAt: {
        label: formatMessage(subscriptionHistoryMessages.renewalExpirationDate),
        id: HISTORY_HEADER.CANCELED_AT,
      },
    }

    if (hasSubscriptionPermission) {
      return {
        ...headersMap,
        paymentInfo: {
          label: formatMessage(subscriptionHistoryMessages.paymentInfoLabel),
          id: HISTORY_HEADER.PAYMENT_INFO,
        },
      }
    }

    return headersMap
  }, [formatMessage, hasSubscriptionPermission])
  const sortOptions = useMemo(() => getSortOptions(headers, true), [headers])

  const [sortBy, setSortBy] = useState<SortingRule<SubscriptionHistoryConfig>>(
    sortOptions[0]
  )
  const columns: readonly Column<SubscriptionHistoryConfig>[] = useMemo(() => {
    if (hasSubscriptionPermission) {
      return [
        {
          Header: 'Date',
          accessor: 'date',
          maxWidth: 90,
          Cell: DateCell,
        },
        {
          Header: 'Event',
          accessor: 'planName',
          Cell: PlanCell,
          maxWidth: 200,
        },
        {
          Header: 'Payment Method',
          accessor: 'paymentInfo',
          Cell: PaymentInfoCell,
          maxWidth: 90,
          sortType: sortByPaymentMethod,
        },
        {
          Header: 'Renewal/Expire Date',
          accessor: 'canceledAt',
          maxWidth: 120,
        },
      ]
    }

    return [
      {
        Header: 'Date',
        accessor: 'date',
        maxWidth: 90,
        Cell: DateCell,
      },
      {
        Header: 'Event',
        accessor: 'planName',
        Cell: PlanCell,
        maxWidth: 200,
      },
      {
        Header: 'Renewal/Expire Date',
        accessor: 'canceledAt',
        maxWidth: 100,
      },
    ]
  }, [hasSubscriptionPermission])

  if (isFetching) {
    return (
      <SpinnerContainer>
        <Spinner />
      </SpinnerContainer>
    )
  }
  return (
    <>
      <HistoryMobileHeader>
        {formatMessage(subscriptionHistoryMessages.mobileTableHeader)}
      </HistoryMobileHeader>
      {isEmpty(props.data) ? (
        <EmptyHistoryContainer>
          <Image
            url={makePublicUrl('images/subscription/empty-history-image.svg')}
          />
          <EmptyHistoryBlock>
            <EmptyHistoryTitle>
              {formatMessage(subscriptionHistoryMessages.noHistoryTitle)}
            </EmptyHistoryTitle>
            <EmptyHistoryDescription>
              {formatMessage(subscriptionHistoryMessages.noHistoryDesc)}
            </EmptyHistoryDescription>
          </EmptyHistoryBlock>
        </EmptyHistoryContainer>
      ) : (
        <>
          <MobileSortBy<SubscriptionHistoryConfig>
            headers={headers}
            sortOptions={sortOptions}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
          <TableWithPaginationWrapper>
            <Table
              columns={columns}
              data={props.data}
              defaultSortBy={DEFAULT_SORT_BY}
              sortBy={sortBy}
              setSortBy={setSortBy}
              mobileViewComponent={MobileHistoryViewComponent}
            />
          </TableWithPaginationWrapper>
        </>
      )}
    </>
  )
}

export default SubscriptionHistoryTable
